import type { ReactNode } from "react";

/**
 * A numbered list of worked steps: a sentence saying what is done, and under it the
 * statement that results, in monospace. A step that changes the meaning of the statement
 * — an inequality sign reversed — is `highlight`ed so it cannot be missed.
 */
export interface WorkedStep {
  text: string;
  math?: string;
  highlight?: boolean;
  note?: ReactNode;
}

const LIST =
  "list-decimal space-y-3 rounded-lg border border-border bg-muted/30 p-4 pl-9 text-sm leading-relaxed marker:text-muted-foreground";

export function StepList({ steps }: { steps: readonly WorkedStep[] }) {
  return (
    <ol className={LIST}>
      {steps.map((step, index) => (
        <li
          // Steps are positional: the same sentence can repeat with different numbers.
          // biome-ignore lint/suspicious/noArrayIndexKey: positional steps
          key={index}
        >
          <p className="text-muted-foreground">{step.text}</p>
          {step.math ? (
            <p className="mt-1 whitespace-pre-wrap break-words font-mono text-foreground">
              {step.math}
            </p>
          ) : null}
          {step.highlight && step.note ? (
            <p className="mt-1 inline-block rounded-md border border-amber-500/50 bg-amber-500/10 px-2 py-1 font-medium text-foreground">
              {step.note}
            </p>
          ) : null}
        </li>
      ))}
    </ol>
  );
}
